import {DateValue} from '@ark-ui/react'
import {parseDate} from '@ark-ui/react/date-picker'
import {
  DoseCalcModeActive,
  OverdosePreventionLevel,
  PatientNameOrder,
  PatientSource,
  VolumeMode,
} from '@cypro/engine/database/schema'
import {PatientProtocol, PatientTherapy} from '@cypro/engine/queries'
import {Patient} from '@cypro/engine/schema'
import {MedicineSchema} from '../protocols/schemas'
import {DrugDetails} from '@cypro/engine/utils'
import {getNumberFormatter} from '@cypro/ui/utils'
import {InstructionNote, MedicineSchemaNew} from './schemas'

export const patientContact = (patient: Patient, contactType: 'home' | 'emergency') => {
  return patient?.contacts?.find(c => c.contactType === contactType)
}

/** Received via HL7 but not imported yet: read-only until imported. */
export const isPendingHl7Import = (patient: Patient) => patient.source === PatientSource.enum.hl7_message

export const getInstructionNoteDefaultValues = (
  duplicate: boolean = false,
  existing?: PatientTherapy
): InstructionNote => {
  return {
    patientProtocolId: existing?.patientProtocolId ? [existing?.patientProtocolId] : ([] as string[]),
    fixedDate: existing?.therapyDate ? [parseDate(new Date(existing.therapyDate))] : ([] as DateValue[]),
    // cycle/day are recomputed from the date once the dialog opens
    cycle: existing?.cycle && !duplicate ? existing.cycle : undefined,
    day: existing?.day && !duplicate ? existing.day : 0,
    time: existing?.time ? existing.time : '',
    isNote: true,
    note: existing?.note || '',
    recurringCycle: false,
    applyToFutureNotes: false,
  }
}

export const mapPatientProtocolsToSelectItems = (protocols: PatientProtocol[]) => {
  return (protocols ?? []).map(pp => ({
    value: pp.id,
    label: `${pp?.protocol?.name} - ${new Date(pp.startDate).toDateString()}`,
  }))
}

export const checkMedicineOverdoseLevel = (
  medicine: PatientTherapy['medicine'][number],
  patientDrugAmountHistory: Record<string, DrugDetails> | undefined
): {overdosePreventionLevel: OverdosePreventionLevel | undefined; limit?: string} | undefined => {
  if (!patientDrugAmountHistory) {
    return undefined
  }
  let overdosePreventionLevel: OverdosePreventionLevel | undefined = undefined
  let reachedLimit = false
  let limit = undefined
  if (
    medicine.activeSubstanceId &&
    patientDrugAmountHistory[medicine.activeSubstanceId]?.overdosePreventionLevel &&
    patientDrugAmountHistory[medicine.activeSubstanceId]?.drugLimits &&
    patientDrugAmountHistory[medicine.activeSubstanceId]?.history?.[medicine.therapyDate]
  ) {
    const drugLimits = patientDrugAmountHistory[medicine.activeSubstanceId].drugLimits
    const limits = patientDrugAmountHistory[medicine.activeSubstanceId].history?.[medicine.therapyDate]
    if (limits && drugLimits) {
      if (drugLimits.perDose && medicine?.totalAmount && medicine?.totalAmount >= drugLimits.perDose) {
        reachedLimit = true
        limit = 'perDose'
      }

      if (drugLimits.perDay && limits.amountInDay >= drugLimits.perDay) {
        reachedLimit = true
        limit = 'perDay'
      }

      if (drugLimits.lifetime && limits.amountToDate >= drugLimits.lifetime) {
        reachedLimit = true
        limit = 'lifetime'
      }
    }
    overdosePreventionLevel = reachedLimit
      ? (patientDrugAmountHistory[medicine.activeSubstanceId].overdosePreventionLevel as OverdosePreventionLevel)
      : undefined
  }

  return {overdosePreventionLevel, limit}
}

export const getParameterDefaultValues = (
  duplicate: boolean = false,
  existing?: PatientTherapy,
  dayZeroProtocol?: boolean | null
) => {
  return {
    patientProtocolId: existing?.patientProtocolId ? [existing?.patientProtocolId] : ([] as string[]),
    fixedDate: existing?.therapyDate ? [parseDate(new Date(existing.therapyDate))] : ([] as DateValue[]),
    day: existing?.day && !duplicate ? existing.day : dayZeroProtocol ? 0 : 1,
    time: existing?.time ? existing.time : '',
    isNote: existing?.isNote || false,
    carrierSolutionId: existing?.carrierSolutionId ? [existing?.carrierSolutionId] : ([] as string[]),
    applicationFormId: existing?.applicationFormId ? [existing?.applicationFormId] : ([] as string[]),
    duration: existing?.duration ? existing.duration : 0,
    volume: existing?.volume ? existing.volume : 0,
    volumeMode: existing?.volumeMode ? [existing?.volumeMode] : ([] as VolumeMode[]),
    dayZeroProtocol: dayZeroProtocol === undefined || dayZeroProtocol === null ? true : dayZeroProtocol,
    medicine:
      existing && existing?.medicine?.length > 0
        ? getParameterDefaultMedicineValues(existing, duplicate)
        : ([
            {
              activeSubstanceId: [] as string[],
              prescribedNameId: [] as string[],
              calculationMethod: [] as DoseCalcModeActive[],
              regularDose: 0,
              precisionDecimals: 2,
              doseProportion: 100,
              notes: '',
              ageRanges: [],
              autIdem: false,
            },
          ] as MedicineSchema[]),
  }
}

export const getParameterDefaultValuesNew = (
  duplicate: boolean = false,
  existing?: PatientTherapy,
  dayZeroProtocol?: boolean | null,
  prescribeByTradeName = false,
  patientResponsibleDoctorId?: string | null
) => {
  return {
    patientProtocolId: existing?.patientProtocolId ? existing?.patientProtocolId : undefined,
    // an existing prescription keeps its own doctor; a new one starts from the patient's responsible doctor
    responsibleDoctorId: existing
      ? (existing.responsibleDoctorId ?? undefined)
      : (patientResponsibleDoctorId ?? undefined),
    fixedDate: existing?.therapyDate ? parseDate(new Date(existing.therapyDate)) : undefined,
    cycle: existing?.cycle && !duplicate ? existing.cycle : undefined,
    day: existing?.day && !duplicate ? existing.day : dayZeroProtocol ? 0 : 1,
    time: existing?.time ? existing.time : '',
    isNote: existing?.isNote || false,
    carrierSolutionId: existing?.carrierSolutionId ? existing?.carrierSolutionId : undefined,
    applicationFormId: existing?.applicationFormId ? existing?.applicationFormId : undefined,
    duration: existing?.duration ? existing.duration : 0,
    volume: existing?.volume ? existing.volume : undefined,
    volumeMode: existing?.volumeMode ? existing?.volumeMode : undefined,
    dayZeroProtocol: dayZeroProtocol === undefined || dayZeroProtocol === null ? true : dayZeroProtocol,
    autIdem: existing?.medicine?.every(m => m.autIdem),
    // organisation setting: open the prescription form with prescribing trade names
    showPrescribingNames: existing?.medicine?.every(m => m.prescribedName) || prescribeByTradeName,
    pharmacyNote: existing?.pharmacyNote ? existing?.pharmacyNote : undefined,
    therapyNote: existing?.therapyNote ? existing?.therapyNote : undefined,
    medicine:
      existing && existing?.medicine?.length > 0
        ? getParameterDefaultMedicineValuesNew(existing, duplicate)
        : ([
            {
              activeSubstanceId: undefined,
              prescribedNameId: undefined,
              calculationMethod: undefined,
              regularDose: 0,
              precisionDecimals: 2,
              doseProportion: 100,
              autIdem: false,
            },
          ] as MedicineSchemaNew[]),
  }
}
export const getParameterDefaultMedicineValuesNew = (existing: PatientTherapy, duplicate: boolean = false) => {
  return existing.medicine.map(medicine => ({
    ...(duplicate !== true && {id: medicine.id}),
    activeSubstanceId: medicine?.activeSubstanceId ? medicine.activeSubstanceId : undefined,
    prescribedNameId: medicine?.prescribedNameId ? medicine.prescribedNameId : undefined,
    calculationMethod: medicine?.calculationMethod ? medicine.calculationMethod : undefined,
    regularDose: medicine?.regularDose ?? 0,
    precisionDecimals: medicine?.precisionDecimals ?? 2,
    doseProportion: medicine?.doseProportion ?? 100,
    autIdem: medicine?.autIdem ?? false,
  }))
}

export const getParameterDefaultMedicineValues = (existing: PatientTherapy, duplicate: boolean = false) => {
  return existing.medicine.map(medicine => ({
    ...(duplicate !== true && {id: medicine.id}),
    activeSubstanceId: medicine?.activeSubstanceId ? [medicine.activeSubstanceId] : ([] as string[]),
    prescribedNameId: medicine?.prescribedNameId ? [medicine.prescribedNameId] : ([] as string[]),
    calculationMethod: medicine?.calculationMethod ? [medicine.calculationMethod] : ([] as DoseCalcModeActive[]),
    regularDose: medicine?.regularDose ?? 0,
    precisionDecimals: medicine?.precisionDecimals ?? 2,
    doseProportion: medicine?.doseProportion ?? 100,
    notes: medicine?.notes ?? '',
    ageRanges: medicine?.ageRanges ?? [],
    autIdem: medicine?.autIdem ?? false,
  }))
}

export function timeDiffLabel(current: string, next: string): string | null {
  if (current === next) return null
  const [currentHours, currentMinutes] = current.split(':').map(Number)
  const [nextHours, nextMinutes] = next.split(':').map(Number)

  if (
    !Number.isInteger(currentHours) ||
    !Number.isInteger(currentMinutes) ||
    !Number.isInteger(nextHours) ||
    !Number.isInteger(nextMinutes)
  ) {
    return null
  }

  const totalDiffMinutes = nextHours * 60 + nextMinutes - (currentHours * 60 + currentMinutes)
  if (totalDiffMinutes === 0) return null

  const sign = totalDiffMinutes > 0 ? '+' : '-'
  const absoluteDiffMinutes = Math.abs(totalDiffMinutes)
  const hours = Math.floor(absoluteDiffMinutes / 60)
  const minutes = absoluteDiffMinutes % 60

  return `(${sign}${hours !== 0 ? `${hours}h` : ''}${minutes !== 0 ? `${minutes}m` : ''})`
}

export function getTodayOrNext(timestamps: string[]): number | null {
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()

  for (const date of timestamps) {
    if (todayStart === Number(date)) {
      return Number(date)
    }
    if (Number(date) >= todayStart) {
      return Number(date)
    }
  }

  return null
}

type PatientNameFields = Pick<Patient, 'givenName' | 'familyName'> & Partial<Pick<Patient, 'fullName'>>

/** without an order the stored full name wins; with one (organisation setting) the name is built in that order */
export function getPatientFullName(patient: PatientNameFields | undefined, order?: PatientNameOrder) {
  if (!patient) return ''
  if (order === PatientNameOrder.enum.LAST_NAME_FIRST) {
    return [patient.familyName, patient.givenName].filter(Boolean).join(', ')
  }
  if (order === PatientNameOrder.enum.FIRST_NAME_LAST) {
    return [patient.givenName, patient.familyName].filter(Boolean).join(' ')
  }
  if (patient.fullName) return patient.fullName
  return [patient.givenName, patient.familyName].filter(Boolean).join(' ')
}

export function getLatestGfrValue(patient: Patient | undefined) {
  return patient?.gfrs?.[0]?.gfr
}

// GFR is displayed with one decimal, and a GFR saved from the GFR dialog is rounded the same way (CY-316)
const GFR_DIGITS = {minimumFractionDigits: 1, maximumFractionDigits: 1}
const gfrFormatter = getNumberFormatter(GFR_DIGITS)
// same rounding as the display (Intl, half away from zero on the stored double), locale-free digits so Number() can read it
const gfrRounder = new Intl.NumberFormat('en-US', {...GFR_DIGITS, useGrouping: false})

export function formatGfr(value: number | null | undefined) {
  return value == null ? '-' : gfrFormatter.format(value)
}

/** The GFR as shown on screen, so a dose from it can be reproduced by hand from the screen. */
export function roundGfr(value: number): number {
  return Number(gfrRounder.format(value))
}

export function displayLatestGfr(patient: Patient | undefined) {
  return formatGfr(getLatestGfrValue(patient))
}
